import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, Form } from 'react-bootstrap';
import { loadGroupByTypeUserGetDataRequest } from '../../group_jobtitles/redux/Actions';
import { loadPowerBIRolesDataRequest } from '../redux/Actions';
import './PowerBISecurity.css';
import DefaultButton from '../../../shared/components/DefaultButton';
import { CANCEL, SAVE } from '../../../shared/constants/Constants';

const PowerBISecurityModal = ({
  show,
  handleClose,
  handleSave,
  mode, // 'add' or 'edit'
  initialData,
  allReports,
  securityList
}) => {
  const dispatch = useDispatch();

  // Get groups and roles from Redux
  const { userGroup = [] } = useSelector((state) => state.groupJobtitle || {});
  const { roles = [] } = useSelector((state) => state.powerBISecurity || {});

  const [formData, setFormData] = useState({
    report_id: '',
    report_name: '',
    report_icon: 'bar-chart', // Default icon
    color_code: '#667eea', // Default color
    show_pages: false, // Default to false
    identity: true, // Default to true
    group_role: []
  });

  const [copyFromReport, setCopyFromReport] = useState('');

  // Load groups and roles when modal opens
  useEffect(() => {
    if (show) {
      dispatch(loadGroupByTypeUserGetDataRequest('user', 'reporting'));
      dispatch(loadPowerBIRolesDataRequest());
    }
  }, [show, dispatch]);

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      // Convert group names to IDs and role labels to codes for backward compatibility
      const normalizedGroupRole = (initialData.group_role || []).map(gr => {
        // Find group by name or ID
        const group = userGroup.find(g =>
          g.id === gr.group ||
          g.group_id === gr.group ||
          g.group_name === gr.group ||
          g.name === gr.group
        );

        // Find role by code or label
        const role = roles.find(r =>
          r.role_code === gr.role ||
          r.code === gr.role ||
          r.role_label === gr.role ||
          r.name === gr.role
        );

        return {
          group: group ? (group.id || group.group_id) : gr.group,
          role: role ? (role.role_code || role.code) : gr.role
        };
      });

      setFormData({
        report_id: initialData.report_id || '',
        report_name: initialData.report_name || '',
        report_icon: initialData.report_icon || initialData.icon || 'bar-chart',
        color_code: initialData.color_code || initialData.color || '#667eea',
        show_pages: initialData.show_pages || false,
        identity: initialData.identity || false,
        group_role: normalizedGroupRole
      });
    } else if (mode === 'add') {
      setFormData({
        report_id: '',
        report_name: '',
        report_icon: 'bar-chart',
        color_code: '#667eea',
        show_pages: false,
        identity: true,
        group_role: []
      });
    }
    setCopyFromReport('');
  }, [mode, initialData, show, userGroup, roles]);

  const handleReportChange = (e) => {
    const reportId = e.target.value;
    const selectedReport = allReports.find(
      r => (r.id || r.report_id || r.reportId) === reportId
    );

    setFormData({
      ...formData,
      report_id: reportId,
      report_name: selectedReport ? (selectedReport.name || selectedReport.reportName || '') : ''
    });
  };

  const handleCopyAccessChange = (e) => {
    const sourceReportId = e.target.value;
    setCopyFromReport(sourceReportId);

    if (sourceReportId) {
      const sourceReport = securityList.find(
        r => (r.report_id || r.id) === sourceReportId
      );

      if (sourceReport && sourceReport.group_role) {
        setFormData({
          ...formData,
          group_role: [...sourceReport.group_role]
        });
      }
    }
  };

  const handleAddGroup = () => {
    setFormData({
      ...formData,
      group_role: [...formData.group_role, { group: '', role: '' }]
    });
  };

  const handleRemoveGroup = (index) => {
    const updatedGroupRole = formData.group_role.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      group_role: updatedGroupRole
    });
  };

  const handleGroupRoleChange = (index, field, value) => {
    const updatedGroupRole = [...formData.group_role];
    updatedGroupRole[index][field] = value;
    setFormData({
      ...formData,
      group_role: updatedGroupRole
    });
  };

  const handleSubmit = () => {
    // Validate that report is selected
    if (!formData.report_id) {
      alert('Please select a report');
      return;
    }

    // Validate that at least one group-role pair is added
    if (formData.group_role.length === 0) {
      alert('Please add at least one group-role pair');
      return;
    }

    // Validate that all group-role pairs are complete
    const hasIncompleteRows = formData.group_role.some(
      gr => !gr.group || !gr.role
    );

    if (hasIncompleteRows) {
      alert('Please complete all group-role selections');
      return;
    }

    handleSave(formData);
  };

  const modalTitle = mode === 'add' ? 'Add New Report' : `Edit Access — ${formData.report_name}`;
  const reportIdLabel = mode === 'edit' ? `Report ID : ${formData.report_id}` : '';

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {modalTitle}
          {reportIdLabel && (
            <div className="text-muted" style={{ fontSize: '0.9rem', fontWeight: 'normal' }}>
              {reportIdLabel}
            </div>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* Report Selection */}
          <Form.Group className="mb-3">
            <Form.Label>
              Report {mode === 'add' && <span className="text-danger">*</span>}
            </Form.Label>
            <Form.Select
              value={formData.report_id}
              onChange={handleReportChange}
              disabled={mode === 'edit'}
              required
            >
              <option value="">Select report...</option>
              {allReports.map((report) => (
                <option
                  key={report.id || report.report_id || report.reportId}
                  value={report.id || report.report_id || report.reportId}
                >
                  {report.name || report.reportName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Auto-filled Report ID */}
          <Form.Group className="mb-3">
            <Form.Label>Report ID</Form.Label>
            <Form.Control
              type="text"
              value={formData.report_id}
              readOnly
              placeholder="Auto-filled"
              style={{ backgroundColor: '#f8f9fa' }}
            />
          </Form.Group>

          {/* Icon Selection */}
          <Form.Group className="mb-3">
            <Form.Label>Report Icon</Form.Label>
            <div className="d-flex align-items-center gap-3">
              <div style={{
                width: '40px',
                height: '40px',
                backgroundColor: '#1a2332',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <i className={`fa fa-${formData.report_icon}`} style={{ fontSize: '1.1rem', color: '#ffffff' }}></i>
              </div>
              <Form.Select
                value={formData.report_icon}
                onChange={(e) => setFormData({ ...formData, report_icon: e.target.value })}
                style={{ flex: 1 }}
              >
                <option value="bar-chart">Bar Chart (fa-bar-chart)</option>
                <option value="line-chart">Line Chart (fa-line-chart)</option>
                <option value="pie-chart">Pie Chart (fa-pie-chart)</option>
                <option value="area-chart">Area Chart (fa-area-chart)</option>
                <option value="table">Table (fa-table)</option>
                <option value="dashboard">Dashboard (fa-dashboard)</option>
                <option value="file-text">File Text (fa-file-text)</option>
                <option value="file-pdf-o">PDF File (fa-file-pdf-o)</option>
                <option value="file-excel-o">Excel File (fa-file-excel-o)</option>
                <option value="briefcase">Briefcase (fa-briefcase)</option>
                <option value="money">Money (fa-money)</option>
                <option value="shopping-cart">Shopping Cart (fa-shopping-cart)</option>
                <option value="users">Users (fa-users)</option>
                <option value="user">User (fa-user)</option>
                <option value="building">Building (fa-building)</option>
                <option value="globe">Globe (fa-globe)</option>
                <option value="cog">Settings (fa-cog)</option>
                <option value="calendar">Calendar (fa-calendar)</option>
                <option value="clock-o">Clock (fa-clock-o)</option>
                <option value="trophy">Trophy (fa-trophy)</option>
                <option value="star">Star (fa-star)</option>
                <option value="heartbeat">Heartbeat (fa-heartbeat)</option>
                <option value="bolt">Bolt (fa-bolt)</option>
                <option value="rocket">Rocket (fa-rocket)</option>
              </Form.Select>
            </div>
            <Form.Text className="text-muted">
              Choose an icon to display in the reports menu
            </Form.Text>
          </Form.Group>

          {/* Checkboxes in one line */}
          <Form.Group className="mb-3">
            <div className="d-flex gap-4">
              <Form.Check
                type="checkbox"
                id="show-pages-checkbox"
                label="Show Pages"
                checked={formData.show_pages}
                onChange={(e) => setFormData({ ...formData, show_pages: e.target.checked })}
              />
              <Form.Check
                type="checkbox"
                id="identity-checkbox"
                label="Identity"
                checked={formData.identity}
                onChange={(e) => setFormData({ ...formData, identity: e.target.checked })}
              />
            </div>
          </Form.Group>

          {/* Job Group Access Section */}
          <Form.Group className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0">Job group access</Form.Label>
              <Form.Select
                style={{ width: 'auto', fontSize: '0.9rem' }}
                value={copyFromReport}
                onChange={handleCopyAccessChange}
              >
                <option value="">Copy access from another report...</option>
                {securityList
                  .filter(r => (r.report_id || r.id) !== formData.report_id)
                  .map((report) => (
                    <option
                      key={report.id}
                      value={report.report_id || report.id}
                    >
                      {report.report_name}
                    </option>
                  ))}
              </Form.Select>
            </div>

            {/* Group Role Table */}
            <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa' }}>
              {formData.group_role.length === 0 ? (
                <div className="text-center text-muted py-3">                  
                </div>
              ) : (
                <div>
                  <div className="row fw-bold mb-2">
                    <div className="col-5">GROUP</div>
                    <div className="col-5">ROLE</div>
                    <div className="col-2"></div>
                  </div>
                  {formData.group_role.map((groupRole, index) => (
                    <div key={index} className="row mb-2 align-items-center">
                      <div className="col-5">
                        <Form.Select
                          size="sm"
                          value={groupRole.group}
                          onChange={(e) =>
                            handleGroupRoleChange(index, 'group', e.target.value)
                          }
                        >
                          <option value="">Select group...</option>
                          {userGroup.map((group) => (
                            <option key={group.id || group.group_id} value={group.id || group.group_id}>
                              {group.group_name || group.name}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                      <div className="col-5">
                        <Form.Select
                          size="sm"
                          value={groupRole.role}
                          onChange={(e) =>
                            handleGroupRoleChange(index, 'role', e.target.value)
                          }
                        >
                          <option value="">Select role...</option>
                          {roles.map((role) => (
                            <option key={role.id || role.role_id} value={role.role_code || role.code}>
                              {role.role_label || role.name}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                      <div className="col-2 text-end">
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-0"
                          onClick={() => handleRemoveGroup(index)}
                          title="Remove"
                        >
                          <i className="fa fa-trash"></i>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Group Button */}
              <div className="text-center mt-3">
                <Button
                  variant="link"
                  onClick={handleAddGroup}
                  className="text-decoration-none"
                >
                  + Add Group & Role
                </Button>
              </div>
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <DefaultButton
          buttonClass="btn btn-secondary"
          buttonTitle={CANCEL}
          actionBlock={handleClose}
        />
        <DefaultButton
          buttonClass="default-btn-color"
          buttonTitle={SAVE}
          actionBlock={handleSubmit}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default PowerBISecurityModal;
